import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const read = (): Theme => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');

/** The current theme, and a toggle that remembers the choice. */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const toggle = useCallback(() => {
    const next = read() === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.toggle('dark', next === 'dark');
    try {
      localStorage.setItem('theme', next);
    } catch {
      // Private mode: the toggle still works for this tab.
    }
  }, []);
  return [theme, toggle];
}

/** Chart colours read from the CSS tokens, re-read when the theme changes (SVG attributes cannot use var()). */
export function useChartColors() {
  const [theme] = useTheme();
  const [colors, setColors] = useState(() => readColors());
  useEffect(() => {
    setColors(readColors());
  }, [theme]);
  return colors;
}

function readColors() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  const rgb = (name: string) => `rgb(${v(name).split(' ').join(',')})`;
  return {
    series: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => v(`--series-${i}`)),
    grid: v('--grid'),
    axis: v('--axis'),
    surface: rgb('--surface'),
    ink: rgb('--ink'),
    muted: rgb('--muted'),
    brand: rgb('--brand'),
    good: rgb('--good'),
    line: rgb('--line')
  };
}
